<script lang="ts">
    import { hostViewportHeight } from '$lib/Stores/stores';
    import { cn } from '$lib/utils/cn';

    import { afterUpdate, onMount, onDestroy } from 'svelte';
    import { writable } from 'svelte/store';
    import { fade, fly } from 'svelte/transition';

    import { resize } from '../_utils/resize';

    import type { Language } from '$lib/types/Language';
    import type { ComponentType } from 'svelte';

    export let open = false;
    export let title = '';
    export let className = '';
    export let contentStyle = '';
    export let handleClose: (event: MouseEvent | TouchEvent) => unknown;
    export let actionButtonsComponent: ComponentType | undefined = undefined;
    export let actionButtonsProps = {};
    export let language: Language = 'en';

    let contentClasses: string;
    let verticalOffsetStyle: string;

    let scrollHeight = writable<number>();
    let translateY = writable(0);
    let withKeyboardAvailableHeight = writable(0);

    // When the keyboard is shown we want to display the bottom sheet above the keyboard, so
    // the user can see the content while typing.
    const resizeHandler = () => {
        const screenHeight = window.innerHeight ?? 0;
        const visualViewportHeight = window.visualViewport?.height ?? 0;
        const visualViewportOffsetTop = window.visualViewport?.offsetTop ?? 0;

        // Available height is the visual viewport while unavailable screen is the height of the keyboard.
        const availableHeight = visualViewportHeight;

        // When mobile devices are not at the bottom of the page, the window.visualViewport adds the offsetTop. It is like the sheet
        // appears from the bottom and when the scroll is not at the bottom then there is a correction.
        const isAtBottom = document.scrollingElement
            ? Math.round(
                  document.scrollingElement.clientHeight + document.scrollingElement.scrollTop,
              ) > document.scrollingElement.scrollHeight
            : false;
        const unavailableHeight =
            screenHeight - availableHeight - (isAtBottom ? 0 : visualViewportOffsetTop);

        const isShowingKeyboard = !!window.visualViewport && screenHeight !== visualViewportHeight;

        translateY.set(unavailableHeight);
        withKeyboardAvailableHeight.set(isShowingKeyboard ? visualViewportHeight : 0);
    };

    onMount(() => {
        resizeHandler();

        window.visualViewport?.addEventListener('resize', resizeHandler);
        window.visualViewport?.addEventListener('scroll', resizeHandler);
        window.addEventListener('touchmove', resizeHandler);
    });

    onDestroy(() => {
        window.visualViewport?.removeEventListener('resize', resizeHandler);
        window.visualViewport?.removeEventListener('scroll', resizeHandler);
        window.removeEventListener('touchmove', resizeHandler);
    });

    afterUpdate(() => {
        document.body.style.overflow = open ? 'hidden' : 'auto';
    });

    $: contentClasses = cn(
        contentStyle,
        'fun-fixed fun-bottom-0 fun-left-0 fun-right-0 fun-z-[1000] fun-flex fun-w-full fun-origin-bottom fun-transform fun-flex-col fun-overflow-hidden fun-rounded-t-3xl fun-bg-white fun-transition-all fun-duration-[500ms] fun-ease-[cubic-bezier(.59,.76,.18,.97)]',
        {
            'fun-bottom-auto': $hostViewportHeight > 0,
        },
    );
    $: {
        verticalOffsetStyle =
            $hostViewportHeight > 0 ? `top: ${$hostViewportHeight - $scrollHeight}px;` : '';
    }
</script>

{#if open}
    <div
        class={cn(
            'fun-size-screen fun-fixed fun-inset-0 fun-top-0 fun-z-[1000] fun-touch-none fun-bg-black/50',
            className,
        )}
        style="fun-transform: translateY(-{$withKeyboardAvailableHeight ? $translateY : 0}px)"
        on:click|stopPropagation={handleClose}
        on:keydown|stopPropagation
        transition:fade={{ duration: 150 }}
        tabindex="-1"
    >
        <div
            class={contentClasses}
            on:click|stopPropagation
            on:keydown|stopPropagation
            style={verticalOffsetStyle +
                ($withKeyboardAvailableHeight
                    ? `max-height: ${$withKeyboardAvailableHeight * 0.95}px;`
                    : 'max-height: 95%;')}
            use:resize={{
                store: scrollHeight,
                updater: (el) => {
                    return el.scrollHeight;
                },
            }}
            transition:fly={{ y: '100vh' }}
            tabindex="-1"
        >
            <div
                class="fun-sticky fun-top-0 fun-z-[1000] fun-flex fun-w-full fun-items-center fun-justify-between fun-border-b fun-bg-white fun-p-6 fun-pb-4 fun-font-ui fun-text-lg fun-font-semibold"
            >
                <p>{title}</p>
                <button
                    aria-label={language === 'de' ? 'Schließen' : 'Close'}
                    class="fun-fill-gray-500"
                    on:click={handleClose}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="24px"
                        height="24px"
                        ><path d="M0 0h24v24H0z" fill="none" />
                        <path
                            d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                        />
                    </svg>
                </button>
            </div>
            <slot />
            {#if !!actionButtonsComponent}
                <div
                    class="fun-bottom-0 fun-left-0 fun-right-0 fun-z-20 fun-w-full fun-border-t fun-border-gray-200 fun-bg-white fun-pt-4"
                >
                    <svelte:component this={actionButtonsComponent} {...actionButtonsProps} />
                </div>
            {/if}
        </div>
    </div>
{/if}
